import { useEffect } from "react";

import { AIServerRoleType } from "components/server-types.ts";
import { useAppDispatch } from "store/index";
import { aiStateActions } from "store/slices/ai";

// AIContextKeyType: a short identifier for one of the defined categories of AI query context.
// When components call `useAIContext(key, data, ...)`, the given data will be formatted into
// an AIContextItem and stored in the AIState.contextItems store under the given key.
export type AIContextKeyType =
  | "assessment_mode"
  | "base"
  | "content_type_explanations"
  | "course_progress_demo"
  | "course_view_explanation"
  | "current_course_content"
  | "current_course_metadata"
  | "current_section_outcomes"
  | "current_page_outcomes"
  | "current_page"
  | "home_view"
  | "outcomes_best_practices"
  | "outcomes_explanation"
  | "page_category"
  | "tool_call_hint" // AI_TOOL_CALL_HINT_CONTEXT_KEY
  | "worksheet" // AI_WORKSHEET_CONTEXT_KEY
  | "worksheet_permissions";

/**
 * This hook ensures that the passed data is available in the AI context,
 * both for chat and for AI actions, for as long as the component is mounted.
 * The context will be removed again when the component unmounts.
 * Clearing the chat will _not_ remove the context.
 * Using the same key twice will overwrite the previous value, allowing
 * children to override the parent's context.
 *
 * Be sure to memoize the data being passed in.
 *
 * To see active context items, use the AIAssistantDebugger
 * visible in the AIChatPanel for MP admins.
 */
export const useAIContext = (
  key: AIContextKeyType,
  data: string | null | false | undefined,
  role: AIServerRoleType,
) => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    const setContext = (truthyData: string) => {
      dispatch(
        aiStateActions.setAiChatData({
          id: key,
          role,
          text: truthyData,
          type: "context",
        }),
      );
    };
    const removeContext = () => {
      dispatch(aiStateActions.removeAiChatData(key));
    };
    if (data) {
      setContext(data);
    } else {
      removeContext();
    }
    // TODO: This results in a lot of deleting/re-adding of state,
    // which is fine, but messes up the ordering in the debugging menu.
    return removeContext;
  }, [dispatch, data, key, role]);
};
